import { useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useGetConfigQuery } from "../../store/settings/settingsService";
import { usePublicConfig } from "../../hooks/usePublicConfig";
import {
  setCartId,
  setModuleCode,
  setShopifyParams,
  setStoreAdminUrl,
  setStoreUrl,
  setWixGetParam,
} from "../../store/settings/settingsSlice";
import { setToken } from "../../store/authentication/authSlice";
import { SHOPIFY_ENV, WIX_ENV } from "../../utils";
import { pickShopifyParams } from "../../utils/shopifyParams";
import LoadingModal from "../../components/LoadingModal";

const SettingsProvider = ({ children }) => {
  const location = useLocation();
  const { env } = usePublicConfig();
  const dispatch = useDispatch();
  const { isLoading, isError, isSuccess } = useGetConfigQuery({});
  const [shopifyJwtHandled, setShopifyJwtHandled] = useState(false);

  const searchParamsObject = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search],
  );

  const configReady = Boolean(env) || isSuccess || isError;
  const isIframe = window.self !== window.top;
  const isShopifyApp = SHOPIFY_ENV?.includes(env?.hostEnv);
  const spjwt = searchParamsObject?.spjwt;
  const blockChildren = !configReady || !shopifyJwtHandled;

  useEffect(() => {
    if (!WIX_ENV?.includes(env?.hostEnv)) return;

    if (Object.keys(searchParamsObject || {}).length > 0) {
      dispatch(setWixGetParam(searchParamsObject));
    }
  }, [searchParamsObject]);

  useLayoutEffect(() => {
    dispatch(setStoreUrl(searchParamsObject?.storeUrl || ""));
  }, [searchParamsObject?.storeUrl]);

  useLayoutEffect(() => {
    dispatch(setStoreAdminUrl(searchParamsObject?.storeAdminUrl || ""));
  }, [searchParamsObject?.storeAdminUrl]);

  useLayoutEffect(() => {
    dispatch(setModuleCode(searchParamsObject?.module_code || ""));
  }, [searchParamsObject?.module_code]);

  useLayoutEffect(() => {
    dispatch(setCartId(searchParamsObject?.cart_id || ""));
  }, [searchParamsObject?.cart_id]);

  useLayoutEffect(() => {
    if (!SHOPIFY_ENV?.includes(env?.hostEnv)) return;

    const shopifyParams = pickShopifyParams(searchParamsObject);

    if (Object.keys(shopifyParams).length > 0) {
      dispatch(setShopifyParams(shopifyParams));
    }
  }, [searchParamsObject, env?.hostEnv]);

  useLayoutEffect(() => {
    if (!configReady) {
      return;
    }

    let skipReason = null;
    let tokenApplied = false;

    if (!spjwt) {
      skipReason = "no-spjwt";
    } else if (!isIframe) {
      skipReason = "not-iframe";
    } else if (!isShopifyApp) {
      skipReason = "not-shopify-hostEnv";
    } else {
      dispatch(setToken(spjwt));
      tokenApplied = true;
    }

    setShopifyJwtHandled(true);
  }, [searchParamsObject?.spjwt, env?.hostEnv, configReady]);

  if (blockChildren) {
    return <LoadingModal isLoading />;
  }

  return children;
};

export default SettingsProvider;
